import React, { useEffect, useState } from 'react';
import { Select, MenuItem, Typography, SelectChangeEvent } from '@mui/material';
import { HormoneSnapshot } from '../../../types';
import { ReactComponent as CalendarIcon } from '../../../images/calendar.svg';
import { parse, format } from 'date-fns';
import { colors, theme } from '../../../utils/theme';

export const DatePicker = ({
  snapshots,
  onDateChange,
}: {
  snapshots: HormoneSnapshot[];
  onDateChange: (snapshot: HormoneSnapshot) => void;
}) => {
  const [selectedDate, setSelectedDate] = useState<string>(
    snapshots[0]?.sample_date || ''
  );

  useEffect(() => {
    if (!selectedDate && snapshots.length) {
      setSelectedDate(snapshots[0]?.sample_date);
    }
  }, [snapshots]);

  const handleChange = (event: SelectChangeEvent<string>) => {
    const newDate = event.target.value;
    setSelectedDate(newDate);
    const newSnapshot = snapshots.find(
      (snapshot) => snapshot.sample_date === newDate
    );
    if (newSnapshot) {
      onDateChange(newSnapshot);
    }
  };

  const formattedDate = (dateString: string) => {
    const parsedDate = parse(dateString);
    return format(parsedDate, 'DD MMM, YYYY');
  };

  return (
    <>
      {selectedDate && (
        <div>
          <Select
            value={selectedDate}
            onChange={handleChange}
            autoWidth
            MenuProps={{
              sx: {
                '& .MuiPaper-root': {
                  boxShadow: 'none',
                  border: `1px solid ${colors.greys.GREY_300}`,
                  mt: 0.75,
                  color: colors.greys.GREY_500,
                  borderRadius: 1.25,
                  '& .Mui-selected': {
                    background: colors.greys.BASE_WHITE,
                    color: theme.palette.info.main,
                  },
                },
                '& .MuiMenu-list': { py: 0 },
                '& .MuiMenuItem-root': {
                  boxShadow: '0px 1px 2px 0px rgba(16, 24, 40, 0.05)',
                  borderRadius: 1.25,
                  py: 1.25,
                  mb: 0.5,
                  '&:hover': {
                    background: colors.greys.HOVER_GREY,
                    borderRadius: 1.25,
                  },
                  '&:active': {
                    border: `1px solid ${theme.palette.info.main}`,
                  },
                  '&:last-child': { boxShadow: 'none', mb: 0 },
                },
              },
            }}
            sx={{
              width: 146,
              '& .MuiOutlinedInput-notchedOutline': {
                border: `1px solid ${colors.greys.GREY_300}`,
                borderRadius: 1.25,
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                border: `1px solid ${colors.greys.GREY_300}`,
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                border: `1px solid ${colors.greys.GREY_300}`,
              },
              '& .MuiInputBase-input': {
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                pl: 2.25,
                py: 1,
                background: colors.greys.BASE_WHITE,
                color: colors.greys.GREY_500,
              },
              '& .MuiSelect-select.MuiInputBase-input.MuiOutlinedInput-input': {
                pr: 2.25,
              },
              '& .MuiSelect-icon': {
                display: 'none',
              },
            }}
          >
            {snapshots?.map((snapshot, index) => (
              <MenuItem value={snapshot?.sample_date} key={index} disableRipple>
                <CalendarIcon />
                <Typography fontSize={14} sx={{ ml: 1 }}>
                  {formattedDate(snapshot?.sample_date)}
                </Typography>
              </MenuItem>
            ))}
          </Select>
        </div>
      )}
    </>
  );
};
